import React, {forwardRef, useCallback, useMemo, useState} from 'react';
import DatePicker from 'react-datepicker';
import {createPortal} from 'react-dom';

import {BASE_Z_INDEX, zIndexStore} from '@pb/kit-component-floating';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {TimeInputWithToggle} from './time-input-with-toggle';
import {CustomCalendarHeader} from './custom-calendar-header';
import {defaultTodayButton} from './default-today-button';
import {getEffectiveDateFormat} from './utils/getEffectiveDateFormat';

import styles from './calendar.component.module.styl';

/**
 * $FlowIgnoreMe WHY: DatePicker has no Flow type definitions
 **/
import type {CustomHeaderProps} from './typedef';
import type {DatePickerProps} from 'react-datepicker';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

/**
 * Props for `<CalendarCore>`: all `react-datepicker` `DatePickerProps` plus two
 * extra fields for the optional time toggle.
 */
export type CalendarCoreProps = DatePickerProps & {
    /** When `true`, the time-input section is visible inside the picker popover. */
    includeTime?: boolean,
    /** Called when the user toggles the time section on or off. Receives the new boolean state. */
    onIncludeTimeChange?: (value: boolean) => void,
};

const Container: React.FC<{children?: React.ReactNode, style?: React.CSSProperties}> = ({children, style}) => {
    return children ? createPortal(
        <div style={style}>{children}</div>,
        document.body,
    ) : null;
};

/**
 * Low-level wrapper around `react-datepicker`'s `DatePicker` with design-system
 * styling, portal-based popover positioning, and an optional time toggle.
 *
 * Prefer `<Calendar>` for most use cases — it adds a text input, locale handling,
 * date normalization, and validation on top of `CalendarCore`. Use `CalendarCore`
 * directly only when you need to fully control the custom header or pop-up container.
 */
export const CalendarCore = forwardRef<DatePicker, CalendarCoreProps>(
    (props, ref) => {
        const {
            calendarClassName,
            showWeekPicker,
            inline = false,
            renderCustomHeader = null,
            todayButton,
            showTimeInput = false,
            includeTime: includeTimeProp,
            onIncludeTimeChange,
            dateFormat,
            onCalendarOpen,
            onCalendarClose,
            ...calendarProps
        } = props;

        const [includeTimeState, setIncludeTimeState] = useState(false);
        const includeTime = includeTimeProp ?? includeTimeState;
        const [isOpen, setIsOpen] = useState<boolean>(false);

        const handleIncludeTimeChange = () => {
            const nextIncludeTime = !includeTime;

            if (includeTimeProp === undefined) {
                setIncludeTimeState(nextIncludeTime);
            }

            onIncludeTimeChange?.(nextIncludeTime);
        };

        const zIndex = useMemo(() => {
            if (isOpen) {
                return zIndexStore.next();
            }

            return BASE_Z_INDEX;
        }, [isOpen]);

        const PopperContainer = useCallback<React.FC<{children?: React.ReactNode}>>(
            ({children}) => (
                <Container style={{'--datepicker-z-index': zIndex} as React.CSSProperties}>
                    {children}
                </Container>
            ), [zIndex]);

        // Convert dateFormat to string if it's an array
        const dateFormatString = useMemo(
            () => (Array.isArray(dateFormat) ? dateFormat[0] : dateFormat),
            [dateFormat],
        );

        // Determine which format to use
        const effectiveFormat = useMemo(
            () => getEffectiveDateFormat(showTimeInput && includeTime, dateFormatString),
            [showTimeInput, includeTime, dateFormatString],
        );

        // Extract time format string directly from effectiveFormat
        const timeFormat = useMemo(() => {
            if (!effectiveFormat) {
                return undefined;
            }

            // Extract time portion from format (everything after date tokens)
            const timeMatch = effectiveFormat.match(/([HhKkmsS:.]+(?:\s*[aA]{1,2})?)$/);

            return timeMatch ? timeMatch[1].trim() : undefined;
        }, [effectiveFormat]);

        const renderHeader = (params: CustomHeaderProps) => <CustomCalendarHeader {...params}/>;

        const calendarClassNames = classNames(
            calendarClassName,
            {
                'react-datepicker_popover': !inline,
                'show-week-picker': showWeekPicker,
            });

        return (
            <div className={classes['calendar-new']}>
                <DatePicker
                    popperClassName={classes['calendar-new-popper']}
                    popperContainer={PopperContainer}
                    {...calendarProps}
                    ref={ref}
                    inline={inline}
                    showWeekPicker={showWeekPicker}
                    renderCustomHeader={renderCustomHeader ?? renderHeader}
                    calendarClassName={calendarClassNames}
                    timeInputLabel={' '}
                    showTimeInput={showTimeInput}
                    onCalendarOpen={() => {
                        setIsOpen(true);
                        onCalendarOpen?.();
                    }}
                    onCalendarClose={() => {
                        setIsOpen(false);
                        onCalendarClose?.();
                    }}
                    customTimeInput={showTimeInput ? (
                        <TimeInputWithToggle
                            timeFormat={timeFormat}
                            includeTime={includeTime}
                            onToggleChange={handleIncludeTimeChange}
                        />
                    ) : undefined}
                    todayButton={
                        todayButton ??
                        defaultTodayButton({
                            selected: calendarProps.selected,
                            maxDate: calendarProps.maxDate,
                            minDate: calendarProps.minDate,
                        })
                    }
                    dateFormat={effectiveFormat}
                />
            </div>
        );
    },
);

CalendarCore.displayName = 'CalendarCore';
